import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiError, publicFileUrl as fileUrl, publicApi } from "@/lib/api";
import ProgressRing from "@/components/ProgressRing";
import { toast } from "sonner";
import {
  Upload, FileText, PenLine, CheckSquare, Info, Loader2, CheckCircle2, Clock, FileCheck2,
  ChevronDown, Calendar, Hash, AlignLeft, Files, Scale, Lock, CreditCard,
} from "lucide-react";

const TYPE_ICON = { document: Upload, multi_file: Files, form: FileText, long_text: AlignLeft, dropdown: ChevronDown, date: Calendar, number: Hash, signature: PenLine, legal: Scale, payment: CreditCard, checkbox: CheckSquare, info: Info };
const STATE_STYLE = {
  pending: { label: "Pending", cls: "bg-secondary text-foreground", icon: Clock },
  submitted: { label: "Waiting approval", cls: "bg-amber-500/15 text-amber-600", icon: FileCheck2 },
  completed: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-600", icon: CheckCircle2 },
};
const fld = "border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary";
const toDataUrl = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

function TaskCard({ token, task, onDone }) {
  const [value, setValue] = useState(task.value || "");
  const [busy, setBusy] = useState(false);
  const Icon = TYPE_ICON[task.type] || Info;
  const st = STATE_STYLE[task.state] || STATE_STYLE.pending;
  const done = task.state === "completed";
  const locked = task.locked;

  const post = async (payload) => {
    setBusy(true);
    try { await publicApi.post(`/public/onboarding/${token}/tasks/${task.task_id}`, payload); toast.success("Submitted"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post(`/public/onboarding/${token}/tasks/${task.task_id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15MB"); return; }
    setBusy(true); post({ file: await toDataUrl(file) });
  };
  const onMultiFile = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 10); if (!files.length) return;
    setBusy(true);
    const urls = []; for (const f of files) urls.push(await toDataUrl(f));
    post({ files: urls });
  };

  return (
    <div className={`bg-card border border-border p-6 ${locked ? "opacity-70" : ""}`} data-testid={`portal-task-${task.task_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-9 w-9 bg-secondary shrink-0"><Icon className="h-4 w-4" /></span>
          <div>
            <h3 className="font-semibold">{task.title}{task.required && <span className="text-accent ml-1">*</span>}</h3>
            {task.description && <p className="text-sm text-muted-foreground mt-0.5">{task.description}</p>}
          </div>
        </div>
        {locked ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-secondary text-muted-foreground"><Lock className="h-3 w-3" /> Locked</span>
        ) : (
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 whitespace-nowrap ${st.cls}`}><st.icon className="h-3 w-3" /> {st.label}</span>
        )}
      </div>

      {locked && <p className="mt-3 pl-12 text-sm text-muted-foreground">Complete the earlier steps to unlock this.</p>}

      {!locked && !done && (
        <div className="mt-4 pl-12 space-y-3">
          {task.type === "legal" && (
            <div className="border border-border bg-secondary/30 p-4 max-h-56 overflow-y-auto thin-scroll text-sm whitespace-pre-wrap font-mono" data-testid={`portal-legal-${task.task_id}`}>
              {task.rendered_body}
            </div>
          )}
          {task.type === "document" && (
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-sm font-medium cursor-pointer hover:bg-secondary transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload file
              <input type="file" className="hidden" onChange={onFile} data-testid={`portal-upload-${task.task_id}`} />
            </label>
          )}
          {task.type === "multi_file" && (
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-sm font-medium cursor-pointer hover:bg-secondary transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Files className="h-4 w-4" />} Upload files
              <input type="file" multiple className="hidden" onChange={onMultiFile} data-testid={`portal-multiupload-${task.task_id}`} />
            </label>
          )}
          {(task.type === "form" || task.type === "date" || task.type === "number") && (
            <div className="flex flex-wrap gap-2">
              <input type={task.type === "date" ? "date" : task.type === "number" ? "number" : "text"} value={value} onChange={(e) => setValue(e.target.value)}
                placeholder="Enter your response" data-testid={`portal-input-${task.task_id}`} className={`flex-1 min-w-[200px] ${fld}`} />
              <button disabled={busy || !String(value).trim()} onClick={() => post({ value: String(value) })} data-testid={`portal-submit-${task.task_id}`} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">Submit</button>
            </div>
          )}
          {task.type === "long_text" && (
            <div className="space-y-2">
              <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={4} placeholder="Type your response…" data-testid={`portal-input-${task.task_id}`} className={`w-full ${fld}`} />
              <button disabled={busy || !value.trim()} onClick={() => post({ value })} data-testid={`portal-submit-${task.task_id}`} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">Submit</button>
            </div>
          )}
          {task.type === "dropdown" && (
            <div className="flex flex-wrap gap-2">
              <select value={value} onChange={(e) => setValue(e.target.value)} data-testid={`portal-input-${task.task_id}`} className={`flex-1 min-w-[200px] ${fld}`}>
                <option value="">Select…</option>
                {(task.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <button disabled={busy || !value} onClick={() => post({ value })} data-testid={`portal-submit-${task.task_id}`} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">Submit</button>
            </div>
          )}
          {(task.type === "signature" || task.type === "legal") && (
            <div className="flex flex-wrap gap-2 items-center">
              <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Type your full name to sign" data-testid={`portal-input-${task.task_id}`} className={`flex-1 min-w-[200px] ${fld} font-medium`} style={{ fontFamily: "cursive" }} />
              <button disabled={busy || !value.trim()} onClick={() => post({ value })} data-testid={`portal-submit-${task.task_id}`} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">Sign & agree</button>
            </div>
          )}
          {(task.type === "checkbox" || task.type === "info") && (
            <button disabled={busy} onClick={() => post({ value: "acknowledged" })} data-testid={`portal-ack-${task.task_id}`} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">{task.type === "info" ? "Acknowledge" : "Mark complete"}</button>
          )}
          {task.type === "payment" && (
            <button disabled={busy} onClick={pay} data-testid={`portal-pay-${task.task_id}`} className="px-4 py-2 bg-accent text-accent-foreground text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay £{Number(task.amount || 0).toFixed(2)}
            </button>
          )}
        </div>
      )}

      {done && task.signature && (
        <p className="mt-3 pl-12 text-sm text-muted-foreground">Signed by <span className="text-foreground font-medium">{task.signature.name}</span> on {new Date(task.signature.signed_at).toLocaleString()}</p>
      )}
      {done && task.value && !task.signature && <p className="mt-3 pl-12 text-sm text-muted-foreground">Your answer: <span className="text-foreground">{task.value}</span></p>}
      {task.doc_path && <div className="mt-2 pl-12"><a href={fileUrl(task.doc_path)} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">View uploaded file</a></div>}
      {task.doc_paths?.length > 0 && <div className="mt-2 pl-12 flex flex-wrap gap-3">{task.doc_paths.map((p, i) => <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">File {i + 1}</a>)}</div>}
    </div>
  );
}

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await publicApi.get(`/public/onboarding/${token}`);
      setData(r.data);
    } catch (e) {
      setErr(apiError(e));
    }
  }, [token]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await publicApi.get(`/public/onboarding/${token}`);
        if (!ignore) setData(r.data);
      } catch (e) {
        if (!ignore) setErr(apiError(e));
      }
    })();
    return () => { ignore = true; };
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("pay_session");
    if (!sess) return;
    (async () => {
      try {
        await publicApi.get(`/public/pay-status/${sess}`);
        toast.success("Payment received");
      } catch (e) { /* ignore */ }
      window.history.replaceState({}, "", `/onboard/${token}`);
      load();
    })();
  }, [token, load]);

  if (err) return <div className="min-h-screen grid place-items-center px-6"><div className="text-center"><h1 className="text-2xl font-bold">Link not found</h1><p className="text-muted-foreground mt-2">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  const { client, organization } = data;
  const stages = [...new Set(client.tasks.map((t) => t.stage_name))];

  if (client.status === "pending_approval" || client.status === "declined") {
    const declined = client.status === "declined";
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
            {organization.logo ? <img src={fileUrl(organization.logo)} alt={organization.name} className="h-8" /> : <span className="font-bold">{organization.name}</span>}
            <span className="overline text-muted-foreground">Powered by OnboardHQ</span>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid={declined ? "portal-declined" : "portal-pending"}>
          <div className={`grid place-items-center h-16 w-16 mx-auto ${declined ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"}`}><Clock className="h-8 w-8" /></div>
          <h1 className="text-2xl font-bold mt-6">{declined ? "Request closed" : `Thanks, ${client.name.split(" ")[0]}!`}</h1>
          <p className="text-muted-foreground mt-3">{declined ? `This onboarding request with ${organization.name} is no longer active. Please contact them if you think this is a mistake.` : `Your request has been received and is awaiting approval from ${organization.name}. We'll email you the moment you can continue.`}</p>
          {declined && client.admin_note && (
            <div className="mt-6 border border-border bg-card px-4 py-3 text-left text-sm" data-testid="portal-decline-reason">
              <p className="font-medium mb-1">Reason from {organization.name}</p>
              <p className="text-muted-foreground">{client.admin_note}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          {organization.logo ? <img src={fileUrl(organization.logo)} alt={organization.name} className="h-8" /> : <span className="font-bold">{organization.name}</span>}
          <span className="overline text-muted-foreground">Powered by OnboardHQ</span>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between gap-6 border border-border bg-card p-6">
          <div>
            <p className="overline text-accent mb-2">{client.flow_name}</p>
            <h1 className="text-2xl font-bold">Hi {client.name.split(" ")[0]}, let's get you set up.</h1>
            <p className="text-muted-foreground mt-1 text-sm">{client.status === "completed" ? "All done — thank you!" : "Complete the tasks below. Your progress is saved automatically."}</p>
          </div>
          <ProgressRing value={client.progress} size={76} />
        </div>
        {client.admin_note && client.status !== "completed" && (
          <div className="mt-4 border border-accent/40 bg-accent/5 px-4 py-3 text-sm" data-testid="portal-welcome-note">
            <span className="font-medium">{organization.name}:</span> {client.admin_note}
          </div>
        )}
        <div className="mt-10 space-y-10">
          {stages.map((stage) => (
            <div key={stage}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{stage}</h2>
              <div className="space-y-3">
                {client.tasks.filter((t) => t.stage_name === stage).map((t) => <TaskCard key={t.task_id} token={token} task={t} onDone={load} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
